import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { ArrowLeft, Award, Briefcase, CalendarDays, Download, GraduationCap, KeyRound, Lock, Scale, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button, Card } from '../components';
import { WalletMeter } from '../components/veiligeHaven/WalletMeter';
import { KlachtenreglementModal } from '../components/klachten/KlachtenreglementModal';
import { kofferApi, KOFFER_KEY, KOFFER_STATUS_KEY, zetKofferTicket, heeftKofferTicket, CHAT_KANALEN_KEY, type KofferInhoud, type TuchtInstantie } from '../services/veiligeHavenApi';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const isVergrendeld = (error: unknown) => isAxiosError(error) && error.response?.status === 423;
const datum = (iso: string) => new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
const pinOk = (p: string) => /^\d{4}$/.test(p);

const PinVeld: React.FC<{ id: string; label: string; waarde: string; onChange: (v: string) => void; autoFocus?: boolean }> = ({ id, label, waarde, onChange, autoFocus }) => (
  <label htmlFor={id} className="block text-sm">
    <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
    <input
      id={id}
      type="password"
      inputMode="numeric"
      autoComplete="off"
      pattern="\d{4}"
      maxLength={4}
      autoFocus={autoFocus}
      value={waarde}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
      className="w-40 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-center text-xl tracking-[0.6em] font-mono"
    />
  </label>
);

/**
 * 💼 Mijn Koffer — de Secure Casus-Vault (onderdeel 60, Onderdeel 5).
 * Optionele 4-cijferige pincode; drie compartimenten: A Mijn Trajecten,
 * B Mijn Academie-Wallet, C Privacy & Escalatie (Wkkgz-klachtenroute).
 */
export const MedewerkerKofferPage: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [ontgrendeld, setOntgrendeld] = useState(heeftKofferTicket);

  const status = useQuery({ queryKey: KOFFER_STATUS_KEY, queryFn: kofferApi.status });
  const dicht = Boolean(status.data?.pin_ingesteld) && !ontgrendeld;
  const inhoud = useQuery({
    queryKey: KOFFER_KEY,
    queryFn: kofferApi.inhoud,
    enabled: status.isSuccess && !dicht,
    retry: (n, e) => !isVergrendeld(e) && n < 2,
  });

  // Ticket verlopen (423): terug naar het slot
  const verlopen = inhoud.isError && isVergrendeld(inhoud.error);
  const vergrendel = () => {
    zetKofferTicket(null);
    setOntgrendeld(false);
    queryClient.removeQueries({ queryKey: KOFFER_KEY });
    status.refetch();
  };

  if (status.isLoading) return <div className="py-16 text-center text-gray-500">…</div>;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/medewerker" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="w-4 h-4" aria-hidden="true" /> {t('algemeen.terug', { defaultValue: 'Terug' })}
        </Link>
        <h1 className="mt-2 text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-[#0B1F3A]" aria-hidden="true" /> {t('koffer.titel', { defaultValue: 'Mijn Koffer' })}
        </h1>
        <p className="text-sm text-gray-600 mt-0.5">
          {t('koffer.intro', { defaultValue: 'Jouw persoonlijke, afgeschermde kluis. Je werkgever kan hier niets van inzien.' })}
        </p>
      </div>

      {dicht || verlopen ? (
        <PinSlot
          geblokkeerdTot={status.data?.geblokkeerd_tot ?? null}
          verlopen={verlopen}
          onOpen={(ticket) => {
            zetKofferTicket(ticket);
            setOntgrendeld(true);
            queryClient.invalidateQueries({ queryKey: KOFFER_KEY });
          }}
        />
      ) : inhoud.isLoading ? (
        <div className="py-10 text-center text-gray-500">…</div>
      ) : inhoud.isError || !inhoud.data ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-red-600" role="alert">{foutMelding(inhoud.error, t('koffer.laadfout', { defaultValue: 'Je koffer kon niet worden geopend.' }))}</p>
          <Button variant="outline" className="mt-4" onClick={() => inhoud.refetch()}>
            {t('algemeen.opnieuw', { defaultValue: 'Opnieuw proberen' })}
          </Button>
        </Card>
      ) : (
        <>
          <CompartimentA data={inhoud.data.trajecten} />
          <CompartimentB data={inhoud.data.academie} />
          <CompartimentC data={inhoud.data.escalatie} />
          <PinBeheer pinIngesteld={Boolean(status.data?.pin_ingesteld)} onGewijzigd={() => status.refetch()} onVergrendel={vergrendel} />
        </>
      )}
    </div>
  );
};

const PinSlot: React.FC<{ geblokkeerdTot: string | null; verlopen: boolean; onOpen: (ticket: string) => void }> = ({ geblokkeerdTot, verlopen, onOpen }) => {
  const { t } = useTranslation('medewerker');
  const [pin, setPin] = useState('');
  const open = useMutation({
    mutationFn: () => kofferApi.ontgrendel(pin),
    onSuccess: (r) => onOpen(r.ticket),
    onError: () => setPin(''),
  });
  return (
    <Card className="p-6 text-center">
      <Lock className="w-10 h-10 text-[#0B1F3A] mx-auto" aria-hidden="true" />
      <h2 className="mt-3 font-semibold text-gray-900">{t('koffer.verzegeld', { defaultValue: 'Je koffer is verzegeld' })}</h2>
      <p className="text-sm text-gray-600 mt-1">
        {verlopen
          ? t('koffer.sessieVerlopen', { defaultValue: 'Voor je veiligheid is je koffer weer op slot gegaan. Voer je pincode opnieuw in.' })
          : t('koffer.voerPinIn', { defaultValue: 'Voer je 4-cijferige pincode in.' })}
      </p>
      <form
        className="mt-4 flex flex-col items-center gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (pinOk(pin)) open.mutate();
        }}
      >
        <PinVeld id="koffer-pin" label={t('koffer.pincode', { defaultValue: 'Pincode' })} waarde={pin} onChange={setPin} autoFocus />
        {geblokkeerdTot && !open.isError && (
          <p className="text-sm text-amber-700">{t('koffer.geblokkeerd', { defaultValue: 'Tijdelijk vergrendeld na te veel foute pogingen.' })}</p>
        )}
        {open.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(open.error, t('koffer.pinFout', { defaultValue: 'Onjuiste pincode.' }))}</p>}
        <Button type="submit" disabled={!pinOk(pin)} loading={open.isPending}>
          {t('koffer.ontgrendel', { defaultValue: 'Ontgrendel' })}
        </Button>
      </form>
    </Card>
  );
};

const Compartiment: React.FC<{ letter: string; titel: string; icon: React.ComponentType<{ className?: string }>; children: React.ReactNode }> = ({ letter, titel, icon: Icon, children }) => (
  <section className="rounded-2xl border border-[#E9E4D8] bg-white p-4 sm:p-5" aria-labelledby={`compartiment-${letter}`}>
    <h2 id={`compartiment-${letter}`} className="flex items-center gap-2 font-semibold text-gray-900">
      <span className="w-7 h-7 rounded-lg bg-[#0B1F3A]/[0.06] text-[#0B1F3A] text-xs font-bold flex items-center justify-center">{letter}</span>
      <Icon className="w-4 h-4 text-gray-500" />
      {titel}
    </h2>
    <div className="mt-3">{children}</div>
  </section>
);

const CompartimentA: React.FC<{ data: KofferInhoud['trajecten'] }> = ({ data }) => {
  const { t } = useTranslation('medewerker');
  const download = useMutation({
    mutationFn: async (a: { id: string; datum: string }) => {
      const blob = await kofferApi.actieplanPdf(a.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `actieplan-${a.datum.slice(0, 10)}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  });
  const statusLabel: Record<string, string> = {
    SCHEDULED: t('koffer.status.SCHEDULED', { defaultValue: 'Gepland' }),
    COMPLETED: t('koffer.status.COMPLETED', { defaultValue: 'Afgerond' }),
    NO_SHOW: t('koffer.status.NO_SHOW', { defaultValue: 'Niet verschenen' }),
  };
  return (
    <Compartiment letter="A" titel={t('koffer.trajecten', { defaultValue: 'Mijn Trajecten' })} icon={CalendarDays}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('koffer.afspraken', { defaultValue: 'Afsprakenhistorie' })}</h3>
      {data.afspraken.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">{t('koffer.geenAfspraken', { defaultValue: 'Nog geen afspraken.' })}</p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-100">
          {data.afspraken.map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 truncate">{a.professional_naam}</span>
                <span className="block text-xs text-gray-500">{a.discipline_label} · {datum(a.start_tijd)}</span>
              </span>
              <span className="text-xs text-gray-600 shrink-0">{statusLabel[a.status] ?? a.status}</span>
            </li>
          ))}
        </ul>
      )}
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('koffer.actieplannen', { defaultValue: 'Actieplannen' })}</h3>
      {data.actieplannen.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">{t('koffer.geenActieplannen', { defaultValue: 'Na een vitaliteitsgesprek vind je hier je persoonlijke actieplan.' })}</p>
      ) : (
        <ul className="mt-1 space-y-1.5">
          {data.actieplannen.map((a) => (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => download.mutate(a)}
                disabled={download.isPending}
                className="w-full flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:border-gray-300 disabled:opacity-60"
              >
                <span>
                  {t('koffer.actieplanVan', { defaultValue: 'Actieplan {{datum}}', datum: datum(a.datum) })}
                  <span className="block text-xs text-gray-500">{a.professional}</span>
                </span>
                <Download className="w-4 h-4 text-gray-500 shrink-0" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {download.isError && <p className="mt-2 text-xs text-red-600" role="alert">{foutMelding(download.error, t('koffer.pdfFout', { defaultValue: 'Het actieplan kon niet worden gedownload.' }))}</p>}
    </Compartiment>
  );
};

const CompartimentB: React.FC<{ data: KofferInhoud['academie'] }> = ({ data }) => {
  const { t } = useTranslation('medewerker');
  return (
    <Compartiment letter="B" titel={t('koffer.academie', { defaultValue: 'Mijn Academie-Wallet' })} icon={GraduationCap}>
      <WalletMeter wallet={data.wallet} />
      <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('koffer.trainingen', { defaultValue: 'Trainingen & certificaten' })}</h3>
      {data.trainingen.length === 0 ? (
        <p className="mt-1 text-sm text-gray-500">
          {t('koffer.geenTrainingen', { defaultValue: 'Nog geen trainingen.' })}{' '}
          <Link to="/medewerker/academy" className="text-[#0B1F3A] font-medium underline">{t('koffer.naarAcademy', { defaultValue: 'Naar de Academy' })}</Link>
        </p>
      ) : (
        <ul className="mt-1 divide-y divide-gray-100">
          {data.trainingen.map((tr) => (
            <li key={tr.id} className="py-2 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block font-medium text-gray-900 truncate">{tr.titel}</span>
                <span className="block text-xs text-gray-500">{tr.categorie_label}</span>
              </span>
              {tr.behaald ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 shrink-0">
                  <Award className="w-3.5 h-3.5" aria-hidden="true" /> {t('koffer.behaald', { defaultValue: 'Behaald' })}
                </span>
              ) : (
                <span className="text-xs text-gray-600 shrink-0">{t('koffer.ingeschreven', { defaultValue: 'Ingeschreven' })}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Compartiment>
  );
};

const KLACHT_STATUS: Record<string, { label: string; kleur: string }> = {
  ingediend: { label: 'Ingediend', kleur: 'bg-amber-50 text-amber-800 border-amber-200' },
  doorgestuurd: { label: 'Doorgestuurd', kleur: 'bg-sky-50 text-sky-800 border-sky-200' },
  afgehandeld: { label: 'Afgehandeld', kleur: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

/** Instantie(s) als tekst, met link waar die er is. */
const Instanties: React.FC<{ lijst: TuchtInstantie[] }> = ({ lijst }) => (
  <>
    {lijst.map((i, n) => (
      <React.Fragment key={i.code}>
        {n > 0 && ' / '}
        {i.url ? (
          <a href={i.url} target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">{i.naam}</a>
        ) : (
          i.naam
        )}
      </React.Fragment>
    ))}
  </>
);

const CompartimentC: React.FC<{ data: KofferInhoud['escalatie'] }> = ({ data }) => {
  const { t } = useTranslation('medewerker');
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [professional, setProfessional] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [gewenst, setGewenst] = useState('');
  const [bevestigd, setBevestigd] = useState(false);
  const [reglementOpen, setReglementOpen] = useState(false);
  const gekozen = data.professionals.find((p) => p.professional_id === professional);

  const indienen = useMutation({
    mutationFn: () => kofferApi.wkkgzKlacht({ professional_id: professional, omschrijving: omschrijving.trim(), gewenste_uitkomst: gewenst.trim() || null }),
    onSuccess: () => {
      setOpen(false);
      setProfessional('');
      setOmschrijving('');
      setGewenst('');
      setBevestigd(false);
      queryClient.invalidateQueries({ queryKey: KOFFER_KEY });
      queryClient.invalidateQueries({ queryKey: CHAT_KANALEN_KEY });
    },
  });

  return (
    <Compartiment letter="C" titel={t('koffer.escalatie', { defaultValue: 'Privacy & Escalatie' })} icon={Scale}>
      <p className="text-sm text-gray-600">
        {t('koffer.wkkgzUitleg', {
          defaultValue:
            'Ontevreden over hoe een professional je heeft behandeld? Start hier de formele klachtenroute. BeLagom legt je klacht voor aan de klachtenfunctionaris en, als jullie er samen niet uitkomen, aan de tucht- of geschilleninstantie waarbij deze professional is aangesloten. Je werkgever krijgt niets te zien.',
        })}
      </p>
      <button type="button" onClick={() => setReglementOpen(true)} className="mt-1 text-xs font-medium text-navy-900 underline hover:opacity-80">
        {t('koffer.reglementLezen', { defaultValue: 'Klachtenreglement voor consumenten lezen (art. 1.3)' })}
      </button>
      <KlachtenreglementModal open={reglementOpen} onClose={() => setReglementOpen(false)} toonFormulierLink={false} variant="consument" />

      {data.klachten.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {data.klachten.map((k) => (
            <li key={k.id} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="block font-mono text-xs text-gray-500">{k.referentie}</span>
                <span className="block text-gray-900 truncate">{k.professional} · {datum(k.ingediend_op)}</span>
                {k.doorgestuurd_naar ? (
                  <span className="block text-xs text-gray-500">
                    {t('koffer.doorgestuurdNaar', { defaultValue: 'Doorgestuurd naar' })} <Instanties lijst={[k.doorgestuurd_naar]} />
                  </span>
                ) : k.instanties.length > 0 && (
                  <span className="block text-xs text-gray-500">
                    {t('koffer.instantie', { defaultValue: 'Instantie' })}: <Instanties lijst={k.instanties} />
                  </span>
                )}
              </span>
              <span className={`rounded-full border px-2 py-0.5 text-xs font-medium shrink-0 ${KLACHT_STATUS[k.status]?.kleur ?? ''}`}>
                {t(`koffer.klachtStatus.${k.status}`, { defaultValue: KLACHT_STATUS[k.status]?.label ?? k.status })}
              </span>
            </li>
          ))}
        </ul>
      )}

      {indienen.isSuccess && (
        <p className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900" role="status">
          {t('koffer.klachtOntvangen', { defaultValue: 'Je klacht {{ref}} is ontvangen. Het gesprek met deze professional is gesloten.', ref: indienen.data.referentie })}
        </p>
      )}

      {data.professionals.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500">{t('koffer.geenProfessionals', { defaultValue: 'Je hebt nog geen contact gehad met een professional.' })}</p>
      ) : !open ? (
        <Button variant="outline" className="mt-3" onClick={() => setOpen(true)}>
          <ShieldAlert className="w-4 h-4 mr-1.5" aria-hidden="true" /> {t('koffer.klachtStarten', { defaultValue: 'Klachtenroute starten' })}
        </Button>
      ) : (
        <form
          className="mt-3 space-y-3 rounded-xl border border-rose-200 bg-rose-50/40 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            indienen.mutate();
          }}
        >
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700 mb-1">{t('koffer.overWie', { defaultValue: 'Over welke professional gaat je klacht?' })}</span>
            <select value={professional} onChange={(e) => setProfessional(e.target.value)} required className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
              <option value="">{t('koffer.kies', { defaultValue: 'Kies…' })}</option>
              {data.professionals.map((p) => (
                <option key={p.professional_id} value={p.professional_id}>{p.naam}</option>
              ))}
            </select>
          </label>
          {gekozen && gekozen.instanties.length > 0 && (
            <p className="text-xs text-gray-600">
              {t('koffer.instantieUitleg', { defaultValue: 'Komen jullie er niet uit, dan gaat je klacht naar:' })} <Instanties lijst={gekozen.instanties} />
            </p>
          )}
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700 mb-1">{t('koffer.watGebeurd', { defaultValue: 'Wat is er gebeurd?' })}</span>
            <textarea value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} rows={5} minLength={20} maxLength={5000} required className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
          </label>
          <label className="block text-sm">
            <span className="block text-xs font-medium text-gray-700 mb-1">{t('koffer.gewenst', { defaultValue: 'Wat zou je willen als uitkomst? (optioneel)' })}</span>
            <textarea value={gewenst} onChange={(e) => setGewenst(e.target.value)} rows={2} maxLength={2000} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" />
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input type="checkbox" checked={bevestigd} onChange={(e) => setBevestigd(e.target.checked)} className="mt-1" />
            <span>
              {t('koffer.bevestig', {
                defaultValue: 'Ik begrijp dat het chatgesprek met deze professional direct en permanent wordt gesloten zodra ik deze klacht indien.',
              })}
            </span>
          </label>
          {indienen.isError && <p className="text-sm text-red-600" role="alert">{foutMelding(indienen.error, t('koffer.klachtFout', { defaultValue: 'Je klacht kon niet worden ingediend.' }))}</p>}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('algemeen.annuleren', { defaultValue: 'Annuleren' })}
            </Button>
            <Button type="submit" variant="danger" disabled={!professional || omschrijving.trim().length < 20 || !bevestigd} loading={indienen.isPending}>
              {t('koffer.klachtIndienen', { defaultValue: 'Klacht indienen' })}
            </Button>
          </div>
        </form>
      )}
    </Compartiment>
  );
};

const PinBeheer: React.FC<{ pinIngesteld: boolean; onGewijzigd: () => void; onVergrendel: () => void }> = ({ pinIngesteld, onGewijzigd, onVergrendel }) => {
  const { t } = useTranslation('medewerker');
  const [modus, setModus] = useState<'rust' | 'instellen' | 'verwijderen'>('rust');
  const [huidig, setHuidig] = useState('');
  const [nieuw, setNieuw] = useState('');
  const [herhaal, setHerhaal] = useState('');
  const reset = () => {
    setModus('rust');
    setHuidig('');
    setNieuw('');
    setHerhaal('');
  };

  const instellen = useMutation({
    mutationFn: () => kofferApi.stelPinIn(nieuw, pinIngesteld ? huidig : undefined),
    onSuccess: (r) => {
      zetKofferTicket(r.ticket);
      reset();
      onGewijzigd();
    },
  });
  const verwijderen = useMutation({
    mutationFn: () => kofferApi.verwijderPin(huidig),
    onSuccess: () => {
      zetKofferTicket(null);
      reset();
      onGewijzigd();
    },
  });
  const fout = instellen.error ?? verwijderen.error;

  return (
    <section className="rounded-2xl border border-dashed border-[#E9E4D8] bg-white/60 p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <KeyRound className="w-4 h-4 text-gray-500" aria-hidden="true" /> {t('koffer.pinVerzegeling', { defaultValue: 'Pincode-verzegeling' })}
        {pinIngesteld && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
            <ShieldCheck className="w-3 h-3" aria-hidden="true" /> {t('koffer.pinAan', { defaultValue: 'Aan' })}
          </span>
        )}
      </h2>
      {modus === 'rust' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setModus('instellen')}>
            {pinIngesteld ? t('koffer.pinWijzigen', { defaultValue: 'Pincode wijzigen' }) : t('koffer.pinInstellen', { defaultValue: 'Pincode instellen' })}
          </Button>
          {pinIngesteld && (
            <>
              <Button size="sm" variant="outline" onClick={() => setModus('verwijderen')}>
                {t('koffer.pinUit', { defaultValue: 'Pincode uitzetten' })}
              </Button>
              <Button size="sm" variant="outline" onClick={onVergrendel}>
                <Lock className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> {t('koffer.nuVergrendelen', { defaultValue: 'Nu vergrendelen' })}
              </Button>
            </>
          )}
        </div>
      ) : (
        <form
          className="mt-3 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (modus === 'instellen') instellen.mutate();
            else verwijderen.mutate();
          }}
        >
          <div className="flex flex-wrap gap-4">
            {pinIngesteld && <PinVeld id="pin-huidig" label={t('koffer.pinHuidig', { defaultValue: 'Huidige pincode' })} waarde={huidig} onChange={setHuidig} autoFocus />}
            {modus === 'instellen' && (
              <>
                <PinVeld id="pin-nieuw" label={t('koffer.pinNieuw', { defaultValue: 'Nieuwe pincode' })} waarde={nieuw} onChange={setNieuw} autoFocus={!pinIngesteld} />
                <PinVeld id="pin-herhaal" label={t('koffer.pinHerhaal', { defaultValue: 'Herhaal pincode' })} waarde={herhaal} onChange={setHerhaal} />
              </>
            )}
          </div>
          {modus === 'instellen' && herhaal.length === 4 && nieuw !== herhaal && (
            <p className="text-xs text-red-600">{t('koffer.pinOngelijk', { defaultValue: 'De pincodes zijn niet gelijk.' })}</p>
          )}
          {fout && <p className="text-sm text-red-600" role="alert">{foutMelding(fout, t('koffer.pinFout', { defaultValue: 'Onjuiste pincode.' }))}</p>}
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline" onClick={reset}>
              {t('algemeen.annuleren', { defaultValue: 'Annuleren' })}
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={(pinIngesteld && !pinOk(huidig)) || (modus === 'instellen' && (!pinOk(nieuw) || nieuw !== herhaal))}
              loading={instellen.isPending || verwijderen.isPending}
            >
              {t('algemeen.opslaan', { defaultValue: 'Opslaan' })}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
};

export default MedewerkerKofferPage;
